import { useState, useMemo, useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { userApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { OrganizationContext } from '../contexts/OrganizationContext'
import { ASSIGNABLE_ROLES, ROLE_HIERARCHY, ROLES, formatRoleLabel } from '../constants/roles'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  DropdownMenu,
  LinkButton,
  PageHeader,
  PageLayout,
  PageToolbar,
  RecordIdentity,
  RecordWorkspace,
  SearchField,
  Select,
  StatusBadge,
  formatStatusLabel,
} from '../components/common'
import UserForm from '../components/UserForm'
import InviteLinkDialog from '../components/InviteLinkDialog'
import styles from './UsersPage.module.css'


export function UsersPage() {
  const { user: currentUser, organizations = [], hasRole } = useAuth()
  const { currentOrg } = useContext(OrganizationContext)
  const queryClient = useQueryClient()

  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [deleteUser, setDeleteUser] = useState(null)
  const [cancelInviteUser, setCancelInviteUser] = useState(null)
  const [inviteLink, setInviteLink] = useState(null)
  const [creatingInviteLink, setCreatingInviteLink] = useState(false)
  // Role changes and deactivation take effect at once and change what people
  // can do or lock them out, so both ask first.
  const [pendingRoleChange, setPendingRoleChange] = useState(null)
  const [deactivateUser, setDeactivateUser] = useState(null)

  // Fetch users
  const { data: users = [], isLoading, isError, error } = useQuery({
    queryKey: ['users', currentOrg?.slug],
    queryFn: () => userApi.list(currentOrg?.slug),
    enabled: !!currentOrg?.slug,
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: (userData) => userApi.create(currentOrg?.slug, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setShowCreateDialog(false)
      toast.success('Invitation sent')
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, updates }) => userApi.update(currentOrg?.slug, userId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setEditingUser(null)
      toast.success('User updated')
    }
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => userApi.updateRole(currentOrg?.slug, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setPendingRoleChange(null)
      toast.success('Role updated')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to update role'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ userId, isActive }) => userApi.toggleActive(currentOrg?.slug, userId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setDeactivateUser(null)
      toast.success('User status updated')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to update user status'),
  })

  const deleteMutation = useMutation({
    mutationFn: (userId) => userApi.delete(currentOrg?.slug, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setDeleteUser(null)
      toast.success('User deleted')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to delete user'),
  })


  const resendInvitationMutation = useMutation({
    mutationFn: (inviteId) => userApi.resendInvitation(currentOrg?.slug, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      toast.success('Invitation resent')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to resend invitation'),
  })

  const cancelInvitationMutation = useMutation({
    mutationFn: (inviteId) => userApi.cancelInvitation(currentOrg?.slug, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      setCancelInviteUser(null)
      toast.success('Invitation cancelled')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to cancel invitation'),
  })

  const resetMFAMutation = useMutation({
    mutationFn: (user) => userApi.resetMFA(currentOrg?.slug, user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
      toast.success('MFA reset. The user will set up a new authenticator at next sign-in.')
    },
    onError: (mutationError) => toast.error(mutationError.response?.data?.error || 'Failed to reset MFA'),
  })

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users
    
    const term = searchTerm.toLowerCase()
    return users.filter(user => {
      const userType = user.is_wager_user ? 'wager team' : 'organization'
      return user.email.toLowerCase().includes(term) ||
        user.display_name?.toLowerCase().includes(term) ||
        user.role.toLowerCase().includes(term) ||
        userType.includes(term)
    })
  }, [users, searchTerm])

  const handleCreateUser = (userData) => {
    createMutation.mutate(userData)
  }

  const handleUpdateUser = (updates) => {
    if (!editingUser) return
    updateMutation.mutate({ userId: editingUser.id, updates })
  }

  const handleRoleChange = (user, role) => {
    if (role === user.role) return
    setPendingRoleChange({ user, role })
  }

  const handleConfirmRoleChange = () => {
    if (!pendingRoleChange) return
    updateRoleMutation.mutate({ userId: pendingRoleChange.user.id, role: pendingRoleChange.role })
  }

  // Activating gives back access the user already had, so it applies at
  // once; deactivating asks first.
  const handleToggleActive = (user) => {
    if (user.is_active) {
      setDeactivateUser(user)
      return
    }
    toggleActiveMutation.mutate({ userId: user.id, isActive: true })
  }

  const handleConfirmDeactivate = () => {
    if (!deactivateUser) return
    toggleActiveMutation.mutate({ userId: deactivateUser.id, isActive: false })
  }

  const handleDeleteUser = () => {
    if (!deleteUser) return
    deleteMutation.mutate(deleteUser.id)
  }

  const handleResendInvitation = (user) => {
    if (!user?.invite_id) return
    resendInvitationMutation.mutate(user.invite_id)
  }

  const handleCreateInvitationLink = async (user) => {
    setCreatingInviteLink(true)
    try {
      const { url, expires_at } = await userApi.createInvitationLink(currentOrg?.slug, user.invite_id)
      setInviteLink({ url, expires_at })
      queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create invite link')
    } finally {
      setCreatingInviteLink(false)
    }
  }

  // Resend and Copy Invite Link each issue a new code, which kills the
  // other's link, so neither starts while the other is in flight.
  const reissuingInvite = resendInvitationMutation.isPending || creatingInviteLink

  const handleCancelInvitation = () => {
    if (!cancelInviteUser?.invite_id) return
    cancelInvitationMutation.mutate(cancelInviteUser.invite_id)
  }


  const currentUserRole = currentOrg?.role || ROLES.VIEWER
  const currentRoleLevel = ROLE_HIERARCHY[currentUserRole] || 0
  const canManageGlobalAccess = organizations.some((organization) => organization.role === ROLES.SUPER_ADMIN)

  const canResetMFA = hasRole(ROLES.SUPER_ADMIN)
  // The delete route is super-admin only; everyone else deactivates instead.
  const canDeleteUsers = hasRole(ROLES.SUPER_ADMIN)
  const isInternalCaller = currentRoleLevel >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const availableRoles = useMemo(() => {
    if (currentUserRole === ROLES.SUPER_ADMIN) return ASSIGNABLE_ROLES
    return ASSIGNABLE_ROLES.filter((r) => ROLE_HIERARCHY[r.value] < currentRoleLevel)
  }, [currentUserRole, currentRoleLevel])

  const isCurrentUser = (userId) => currentUser?.id === userId
  const isSuperAdmin = (user) => user.role === ROLES.SUPER_ADMIN
  const hasPendingInvitation = (user) => user.invite_status === 'pending' && !!user.invite_id
  // An expired invitation can still be resent (which renews it) or cancelled.
  const isExpiredInvitation = (user) => (
    hasPendingInvitation(user)
    && !!user.invite_expires_at
    && new Date(user.invite_expires_at).getTime() <= Date.now()
  )
  // The API refuses changes to a member whose role is equal to or above the
  // caller's (super admins excepted), so those rows are read-only here too.
  const outranksCaller = (user) => (
    currentUserRole !== ROLES.SUPER_ADMIN && (ROLE_HIERARCHY[user.role] || 0) >= currentRoleLevel
  )
  const isReadOnlyMember = (user) => (
    user.is_wager_user || isSuperAdmin(user) || isCurrentUser(user.id) || outranksCaller(user)
  )

  const columns = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      sticky: 'start',
      render: (row) => (
        <RecordIdentity
          className={styles.userIdentity}
          title={row.email}
          subtitle={row.display_name || 'No display name'}
          titleAttribute={row.email}
          badges={row.is_wager_user ? <Badge compact className={styles.mobileWagerBadge}>Wager</Badge> : null}
        />
      ),
    },
    {
      key: 'user_type',
      label: 'User type',
      priority: 'tertiary',
      render: (row) => <Badge compact>{row.is_wager_user ? 'Wager team' : 'Organization'}</Badge>,
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => {
        if (isReadOnlyMember(row)) {
          return (
            <StatusBadge status="default">
              {formatRoleLabel(row.role)}
            </StatusBadge>
          )
        }

        return (
          <Select
            value={row.role}
            onChange={(e) => handleRoleChange(row, e.target.value)}
            className={styles.roleSelect}
            size="compact"
            aria-label={`Change role for ${row.display_name || row.email}`}
            disabled={updateRoleMutation.isPending}
          >
            {availableRoles.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </Select>
        )
      }
    },
    {
      key: 'invite_status',
      label: 'Invitation',
      priority: 'secondary',
      render: (row) => {
        if (!row.invite_status) {
          return <span className={styles.invitePlaceholder}>-</span>
        }

        const variants = {
          pending: 'warning',
          accepted: 'success',
          cancelled: 'default',
        }

        const expired = isExpiredInvitation(row)
        return (
          <div className={styles.invitation}>
            <StatusBadge status={expired ? 'error' : variants[row.invite_status] || 'default'}>
              {expired ? 'Expired' : formatStatusLabel(row.invite_status)}
            </StatusBadge>
            {hasPendingInvitation(row) && row.invite_expires_at ? (
              <span className={styles.invitePlaceholder}>
                {expired ? 'Since' : 'Expires'} {new Date(row.invite_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            ) : null}
          </div>
        )
      }
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => {
        // An invited user becomes active when they accept; the Invitation
        // column already says the invite is pending.
        if (hasPendingInvitation(row)) {
          return <span className={styles.invitePlaceholder}>-</span>
        }
        return (
          <StatusBadge status={row.is_active ? 'success' : 'error'}>
            {row.is_active ? 'Active' : 'Inactive'}
          </StatusBadge>
        )
      }
    },
    {
      key: 'mfa_enabled',
      label: 'MFA',
      priority: 'tertiary',
      render: (row) => (
        row.mfa_enabled ? (
          <StatusBadge status="info">Enabled</StatusBadge>
        ) : (
          <span className={styles.mfaDisabled}>—</span>
        )
      )
    },
    {
      key: 'last_login_at',
      label: 'Last Login',
      priority: 'tertiary',
      render: (row) => {
        if (!row.last_login_at) return 'Never'
        return new Date(row.last_login_at).toLocaleDateString()
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sticky: 'end',
      render: (row) => {
        const pendingInvitation = hasPendingInvitation(row)
        if (row.is_wager_user) {
          return canManageGlobalAccess ? (
            <LinkButton href="/command-center/wager-team" variant="ghost" size="compact">
              Manage access
            </LinkButton>
          ) : (
            <span className={styles.invitePlaceholder}>View only</span>
          )
        }
        // An external admin's own row is read-only too: the API refuses an
        // equal role, the caller included. Internal roles may edit their own.
        if (outranksCaller(row) && (!isCurrentUser(row.id) || !isInternalCaller)) {
          return <span className={styles.invitePlaceholder}>View only</span>
        }

        const menuItems = [
          {
            key: 'delete',
            label: 'Delete',
            onClick: () => setDeleteUser(row),
            disabled: isCurrentUser(row.id) || isSuperAdmin(row),
            danger: true,
            hidden: !canDeleteUsers,
          },
          {
            key: 'resend-invite',
            label: 'Resend Invite',
            onClick: () => handleResendInvitation(row),
            disabled: reissuingInvite,
            hidden: !pendingInvitation,
          },
          {
            key: 'copy-invite-link',
            label: 'Copy Invite Link',
            onClick: () => handleCreateInvitationLink(row),
            disabled: reissuingInvite,
            hidden: !pendingInvitation || !isInternalCaller,
          },
          {
            key: 'cancel-invite',
            label: 'Cancel Invite',
            onClick: () => setCancelInviteUser(row),
            disabled: cancelInvitationMutation.isPending,
            danger: true,
            hidden: !pendingInvitation,
          },
          {
            key: 'reset-mfa',
            label: 'Reset MFA',
            onClick: () => resetMFAMutation.mutate(row),
            disabled: resetMFAMutation.isPending,
            hidden: !row.is_active || !canResetMFA,
          },
          {
            key: 'toggle-active',
            label: row.is_active ? 'Deactivate' : 'Activate',
            onClick: () => handleToggleActive(row),
            disabled: isCurrentUser(row.id) || isSuperAdmin(row),
            hidden: pendingInvitation,
          },
        ]

        return (
          <div className={styles.actions}>
            <Button
              onClick={() => setEditingUser(row)}
              variant="ghost"
              size="compact"
              aria-label={`Edit ${row.display_name || row.email}`}
            >
              Edit
            </Button>

            <DropdownMenu items={menuItems} triggerLabel="More actions" />
          </div>
        )
      }
    }
  ]

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Users"
        context={currentOrg?.name}
        subtitle={isInternalCaller
          ? 'Organization users can be managed here. Wager team access is read-only and managed in Command Center.'
          : 'Invite and manage the people in your organization who use Studio.'}
        secondaryActions={canManageGlobalAccess ? (
          <LinkButton href="/command-center/wager-team" variant="secondary">Manage Wager team</LinkButton>
        ) : null}
        primaryAction={<Button onClick={() => setShowCreateDialog(true)} disabled={!currentOrg}>Invite user</Button>}
      />

      <RecordWorkspace
        label="User records"
        toolbar={(
          <PageToolbar
            label="User controls"
            search={<SearchField value={searchTerm} onChange={setSearchTerm} label="Search users" placeholder="Search users…" />}
            actions={<Badge compact>{filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}</Badge>}
          />
        )}
        loading={Boolean(currentOrg) && isLoading}
        error={isError ? error : null}
        restricted={!currentOrg}
        items={filteredUsers}
        filtered={Boolean(searchTerm.trim())}
        emptyTitle="No users yet"
        emptyDescription={`Invite a user to ${currentOrg?.name || 'this organization'}.`}
        searchEmptyTitle="No users match your search"
        onRetry={() => queryClient.invalidateQueries({ queryKey: ['users', currentOrg?.slug] })}
        onClear={() => setSearchTerm('')}
      >
        <DataTable
          columns={columns}
          data={filteredUsers}
          loading={Boolean(currentOrg) && isLoading}
          loadingMessage="Loading users…"
          keyField="id"
          density="compact"
          label={`${currentOrg?.name || 'Organization'} users`}
          responsiveColumns
        />
      </RecordWorkspace>

      {showCreateDialog && (
        <UserForm
          roleOptions={availableRoles}
          onSubmit={handleCreateUser}
          onCancel={() => setShowCreateDialog(false)}
          isSubmitting={createMutation.isPending}
          error={createMutation.error}
        />
      )}

      {editingUser && (
        <UserForm
          user={editingUser}
          onSubmit={handleUpdateUser}
          onCancel={() => setEditingUser(null)}
          isSubmitting={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}

      {deleteUser && (
        <ConfirmDialog
          open={!!deleteUser}
          title="Delete user"
          message={`Are you sure you want to delete this user? This will remove ${deleteUser.email} from the organization.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDeleteUser}
          onCancel={() => setDeleteUser(null)}
          isDestructive
        />
      )}

      {pendingRoleChange && (
        <ConfirmDialog
          open={!!pendingRoleChange}
          title="Change role"
          message={`Change ${pendingRoleChange.user.display_name || pendingRoleChange.user.email} from ${formatRoleLabel(pendingRoleChange.user.role)} to ${formatRoleLabel(pendingRoleChange.role)}? Their access changes right away.`}
          confirmLabel="Change role"
          cancelLabel="Cancel"
          loading={updateRoleMutation.isPending}
          onConfirm={handleConfirmRoleChange}
          onCancel={() => setPendingRoleChange(null)}
        />
      )}

      {deactivateUser && (
        <ConfirmDialog
          open={!!deactivateUser}
          title="Deactivate user"
          message={`Deactivate ${deactivateUser.display_name || deactivateUser.email}? They lose access to ${currentOrg?.name || 'this organization'} until someone activates them again.`}
          confirmLabel="Deactivate"
          cancelLabel="Cancel"
          loading={toggleActiveMutation.isPending}
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setDeactivateUser(null)}
          isDestructive
        />
      )}

      {inviteLink ? (
        <InviteLinkDialog url={inviteLink.url} expiresAt={inviteLink.expires_at} onClose={() => setInviteLink(null)} />
      ) : null}

      {cancelInviteUser && (
        <ConfirmDialog
          open={!!cancelInviteUser}
          title="Cancel Invitation"
          message={`Are you sure you want to cancel the invitation for ${cancelInviteUser.email}?`}
          confirmLabel="Cancel Invitation"
          cancelLabel="Keep Invitation"
          onConfirm={handleCancelInvitation}
          onCancel={() => setCancelInviteUser(null)}
          isDestructive
        />
      )}

    </PageLayout>
  )
}

export default UsersPage
